import React, { useState, useCallback, useEffect } from "react";
import { Tooltip } from "@material-ui/core";

export interface TruncatedTooltipProps {
    title: React.ReactNode;
    tooltipClasses?: { tooltip?: string };
    enterDelay?: number;
    enterNextDelay?: number;
    children: React.ReactElement;
}

export function TruncatedTooltip({ title, tooltipClasses, enterDelay, enterNextDelay, children }: TruncatedTooltipProps) {
    const [isTruncated, setIsTruncated] = useState(false);
    const [node, setNode] = useState<HTMLElement | null>(null);

    const refCallback = useCallback((el: HTMLElement | null) => {
        setNode(el);
    }, []);

    useEffect(() => {
        if (!node) {
            setIsTruncated(false);
            return;
        }

        const checkTruncation = () => {
            setIsTruncated(node.scrollWidth - node.clientWidth > 1);
        };

        checkTruncation();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", checkTruncation);
            return () => window.removeEventListener("resize", checkTruncation);
        }

        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(node);

        return () => resizeObserver.disconnect();
    }, [node]);

    const childWithRef = React.isValidElement(children)
        ? React.cloneElement(children, { ref: refCallback } as React.HTMLAttributes<HTMLElement>)
        : children;

    return (
        <Tooltip
            title={title}
            placement="top"
            disableHoverListener={!isTruncated}
            classes={tooltipClasses}
            enterDelay={enterDelay}
            enterNextDelay={enterNextDelay}
        >
            {childWithRef}
        </Tooltip>
    );
}
